import { createContext, useContext, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import type { LineCitation } from '../../services/ai/citations/citationTypes'
import { openLineCitationTarget, type LineCitationSurface, type OpenLineCitationOptions } from './openLineCitationTarget'

/**
 * A tiny provider that lets any citation chip open a source without knowing
 * about TimelineContext / attachment viewer / chat scroll. The app root
 * (desktop AND mobile) registers callbacks once; chips consume via hook.
 */

interface CitationDispatchValue {
  surface: LineCitationSurface
  handlers: Omit<OpenLineCitationOptions, 'surface'>
}

const CitationDispatchContext = createContext<CitationDispatchValue | null>(null)

export const CitationDispatchProvider = ({
  surface,
  onFocusTimelineNode,
  onOpenUpload,
  onScrollToChatTurn,
  onOpenMeeting,
  onOpenReport,
  onOpenGeneric,
  children,
}: {
  surface: LineCitationSurface
  onFocusTimelineNode?: OpenLineCitationOptions['onFocusTimelineNode']
  onOpenUpload?: OpenLineCitationOptions['onOpenUpload']
  onScrollToChatTurn?: OpenLineCitationOptions['onScrollToChatTurn']
  onOpenMeeting?: OpenLineCitationOptions['onOpenMeeting']
  onOpenReport?: OpenLineCitationOptions['onOpenReport']
  onOpenGeneric?: OpenLineCitationOptions['onOpenGeneric']
  children: ReactNode
}) => {
  // Keep handlers in a ref so the context value is stable across renders
  // yet always fires the latest callback closure.
  const handlersRef = useRef<Omit<OpenLineCitationOptions, 'surface'>>({})
  handlersRef.current = { onFocusTimelineNode, onOpenUpload, onScrollToChatTurn, onOpenMeeting, onOpenReport, onOpenGeneric }
  useEffect(() => () => { handlersRef.current = {} }, [])
  const value = useMemo<CitationDispatchValue>(() => ({
    surface,
    handlers: {
      onFocusTimelineNode: (nodeId, board) => handlersRef.current.onFocusTimelineNode?.(nodeId, board),
      onOpenUpload: (uploadId) => handlersRef.current.onOpenUpload?.(uploadId),
      onScrollToChatTurn: (turnId) => handlersRef.current.onScrollToChatTurn?.(turnId),
      onOpenMeeting: (meetingId) => handlersRef.current.onOpenMeeting?.(meetingId),
      onOpenReport: (reportId) => handlersRef.current.onOpenReport?.(reportId),
      onOpenGeneric: (citation) => handlersRef.current.onOpenGeneric?.(citation),
    },
  }), [surface])
  return <CitationDispatchContext.Provider value={value}>{children}</CitationDispatchContext.Provider>
}

export const useCitationDispatch = () => {
  const ctx = useContext(CitationDispatchContext)
  return useCallback((citation: LineCitation) => {
    if (!ctx) return false
    return openLineCitationTarget(citation, { surface: ctx.surface, ...ctx.handlers })
  }, [ctx])
}

export const useCitationSurface = (): LineCitationSurface => useContext(CitationDispatchContext)?.surface ?? 'desktop'
